import {moment} from 'moment';
import {_} from 'lodash'
import * as ko from "knockout"
import {IMutableCard} from "../../index"
import {ICompany} from "../../../../../index"
import {INPUT_DATE_FORMAT, Permission} from "../../../../../constants"
import {nsUtils} from "../../nsUtils"
import {nsDate} from "../../nsDate"
import {AbstractMutableData} from "../AbstractMutableData";
import {IInputObjectParams, InputObject} from "../../InputObject";
import {StatusManager} from "../../StatusManager";

export class MutableImportFilterData extends AbstractMutableData implements IMutableCard {
    public readonly input_maxnew: InputObject<ICompany['maxnew']>
    public readonly input_maxedit: InputObject<ICompany['maxedit']>
    public readonly input_maxdeletes: InputObject<ICompany['maxdeletes']>
    private isActive_original!: boolean

    constructor(statusManager: StatusManager) {
        super(statusManager)

        const getThreshold = (defaults: any) => {
            return _.defaultsDeep(defaults, {
                value: null,
                type: 'number',
                attr: {min: 0, max: 100},
                isValid: (num: number) => !isNaN(num) && num >= 0 && num <= 100 && parseInt(String(num)) == num,
                event: {blur: (obj: InputObject<number>) => obj.obs(obj.obs() || 0)},
            } as unknown as IInputObjectParams<number>)
        }

        this.input_maxnew = new InputObject(getThreshold({ph: 'New|p', value: this.statusManager.company.maxnew}))
        this.input_maxedit = new InputObject(getThreshold({ph: 'Changed', value: this.statusManager.company.maxedit}))
        this.input_maxdeletes = new InputObject(getThreshold({ph: 'Deleted', value: this.statusManager.company.maxdeletes}))

        this.kosub.subscribe(this.isActive, bool => {
            if (bool) {
                this.input_date('')
            } else {
                const m = nsDate.getNextWorkDay()
                this.input_date(m.format(INPUT_DATE_FORMAT))
            }
        })
    }

    public override init() {
        this.minDate = moment().startOf('day').add(1, 'days').format(INPUT_DATE_FORMAT)
        this.maxDate = moment().startOf('day').add(30, 'days').format(INPUT_DATE_FORMAT)
        const ignoreimportfilteruntil = this.statusManager.company.ignoreimportfilteruntil;
        const active = !this.statusManager.company.ignoreimportfilter && nsUtils.isNullOrUndefinedOrEmptyString(ignoreimportfilteruntil);
        const date = nsUtils.isNullOrUndefinedOrEmptyString(ignoreimportfilteruntil) ? '' : nsDate.convertFromServerString(ignoreimportfilteruntil).format(INPUT_DATE_FORMAT)
        this.isActive_original = active
        this.isActive(active)
        this.input_date(date)
        this.input_maxnew.obs(this.statusManager.company.maxnew)
        this.input_maxedit.obs(this.statusManager.company.maxedit)
        this.input_maxdeletes.obs(this.statusManager.company.maxdeletes)
        super.init()
    }

    public isChanged = ko.pureComputed(() => {
        const data = this.getSubmitData()
        const originalCompany = this.statusManager.company;
        const a = data.maxnew != originalCompany.maxnew
        const b = data.maxedit != originalCompany.maxedit
        const c = data.maxdeletes != originalCompany.maxdeletes
        const d = data.ignoreimportfilter != originalCompany.ignoreimportfilter
        const e = data.ignoreimportfilteruntil != originalCompany.ignoreimportfilteruntil
        return a || b || c || d || e
    })

    public isValid = ko.pureComputed(() => {
        if (this.crudMode() == 'read') {
            return true
        }
        const a = this.isInputValid('maxnew')
        const b = this.isInputValid('maxedit')
        const c = this.isInputValid('maxdeletes')
        const d = this.isInputValid('ignoreimportfilteruntil')
        return !!(a && b && c && d)
    })

    public isInputValid(key: keyof ICompany) {
        const val = this.getSubmitData()[key]
        switch (key) {
            case "maxdeletes":
                return this.input_maxdeletes.isValid()
            case "maxedit":
                return this.input_maxedit.isValid()
            case "maxnew": {
                return this.input_maxnew.isValid()
            }
            case "ignoreimportfilteruntil": {
                if (this.isActive()) {
                    return true
                }
                const m = moment(this.input_date(), INPUT_DATE_FORMAT)
                const valid = m.isValid();
                const diff = m.diff(moment().startOf('day'), 'days');
                const min = diff >= 1;
                const max = diff <= 30;
                return valid && min && max
            }
        }
        return false
    }

    public contentMode = ko.pureComputed((): TContentMode => {
        const write = this.crudMode() == 'write'
        const permission = this.permission() >= Permission.CompanyAdmin

        // Thresholds stay visible/editable whether the filter is on or off.
        // Date ("turn off until") is shown separately in the template when inactive.
        if (write && permission) {
            return 'writeThresholds'
        }
        return 'readThresholds'
    })

    public getSubmitData = ko.pureComputed((): ICompany => {
        const active = this.isActive()
        const isActiveChanged = active != this.isActive_original
        const company = this.statusManager.company

        let importfilterdeactivated: string
        let importfilterdeactivatedby: number
        let importfilterdeactivatedbyorgnr: string

        if (!isActiveChanged) {
            importfilterdeactivated = company.importfilterdeactivated ?? ''
            importfilterdeactivatedby = company.importfilterdeactivatedby ?? 0
            importfilterdeactivatedbyorgnr = company.importfilterdeactivatedbyorgnr ?? ''
        } else if (!active) {
            importfilterdeactivated = nsDate.convertToServerString(moment())
            importfilterdeactivatedby = this.loginObj.id
            importfilterdeactivatedbyorgnr = this.loginObj.orgnr
        } else {
            importfilterdeactivated = ''
            importfilterdeactivatedby = 0
            importfilterdeactivatedbyorgnr = ''
        }

        const defaults = this.mutableCompany.getSubmitData();
        return _.defaultsDeep({
            maxedit: Number(this.input_maxedit.obs()),
            maxnew: Number(this.input_maxnew.obs()),
            maxdeletes: Number(this.input_maxdeletes.obs()),
            ignoreimportfilteruntil: !active ? nsDate.convertToServerString(moment(this.input_date(), INPUT_DATE_FORMAT)) : '',
            ignoreimportfilter: !active,
            importfilterdeactivated,
            importfilterdeactivatedby,
            importfilterdeactivatedbyorgnr,
        } as Partial<ICompany>, defaults)
    })

}